// Constants derived from the Invitae Cardiology Requisition Form (TRF904-20)

import type { FamilyHistoryRow } from './formData'
export type { FamilyHistoryRow }

export const INVITAE_CARDIOLOGY_QUESTIONNAIRE_ID = 'invitae-cardiology-trf904-20'

export const CARDIOLOGY_SPECIMEN_TYPES = [
  { value: 'blood', label: 'Blood (3-mL purple EDTA)' },
  { value: 'saliva', label: 'Saliva (Oragene kit)' },
  { value: 'dna', label: 'DNA' },
] as const

export const CARDIOLOGY_PRIMARY_INDICATIONS = [
  { value: 'aortopathy', label: 'Aortopathy' },
  { value: 'arrhythmia', label: 'Arrhythmia' },
  { value: 'cardiomyopathy', label: 'Cardiomyopathy' },
  { value: 'neurology', label: 'Neurology' },
  { value: 'other', label: 'Other' },
] as const

// Clinical diagnosis conditions from page 2 of TRF904-20
export const CLINICAL_DIAGNOSES = [
  {
    value: 'arvc',
    label:
      'Arrhythmogenic right ventricular cardiomyopathy (ARVC) / Arrhythmogenic cardiomyopathy (ACM)',
  },
  { value: 'brugada', label: 'Brugada syndrome (BrS)' },
  { value: 'cm-avms', label: 'Capillary malformation-arteriovenous malformations (CM-AVMs)' },
  { value: 'cpvt', label: 'Catecholaminergic Polymorphic Ventricular Tachycardia (CPVT)' },
  { value: 'congenital-heart-defect', label: 'Congenital heart defect' },
  { value: 'dcm', label: 'Dilated cardiomyopathy (DCM)' },
  { value: 'early-onset-af', label: 'Early-onset atrial fibrillation (AF)' },
  { value: 'eds', label: 'Ehlers-Danlos syndrome (EDS)' },
  { value: 'fh', label: 'Familial hypercholesterolemia (FH)' },
  { value: 'hht', label: 'Hereditary hemorrhagic telangiectasia (HHT)' },
  { value: 'hcm', label: 'Hypertrophic cardiomyopathy (HCM)' },
  { value: 'lvnc', label: 'Left ventricular noncompaction cardiomyopathy (LVNC)' },
  { value: 'lds', label: 'Loeys-Dietz syndrome (LDS)' },
  { value: 'lqts', label: 'Long QT syndrome (LQTS)' },
  { value: 'marfan', label: 'Marfan syndrome' },
  { value: 'pccd', label: 'Progressive cardiac conduction disease (PCCD)' },
  { value: 'pah', label: 'Pulmonary arterial hypertension (PAH)' },
  { value: 'rcm', label: 'Restrictive cardiomyopathy (RCM)' },
  { value: 'sqts', label: 'Short QT syndrome (SQTS)' },
  { value: 'sudden-cardiac-arrest', label: 'Sudden cardiac arrest / Sudden unexplained death' },
  { value: 'taad', label: 'Thoracic aortic aneurysm / dissection (TAAD)' },
  { value: 'attr', label: 'Transthyretin-mediated amyloidosis (ATTR)' },
  { value: 'other', label: 'Other' },
] as const

// Clinical Information Checklist — 9 categories from page 2 of TRF904-20
export interface ClinicalInfoItem {
  value: string
  label: string
  hasTextField?: boolean // Items with a fill-in text field
  subItems?: { value: string; label: string }[]
  selectType?: 'radio' | 'checkbox' // For sub-items that are radio vs checkbox groups
}

export interface ClinicalInfoCategory {
  category: string
  hasYesNoUnknown: boolean
  items: ClinicalInfoItem[]
}

export const CLINICAL_INFO_CATEGORIES: ClinicalInfoCategory[] = [
  {
    category: 'Aortopathy/Connective Tissue Disorder',
    hasYesNoUnknown: true,
    items: [
      { value: 'aortic-arterial-aneurysm', label: 'Aortic/Arterial aneurysm' },
      {
        value: 'aortic-arterial-dissection',
        label: 'Aortic/Arterial dissection',
        hasTextField: true,
      },
      { value: 'aortic-root-dilation', label: 'Aortic root dilation', hasTextField: true },
      { value: 'arachnodactyly', label: 'Arachnodactyly' },
      { value: 'arterial-tortuosity', label: 'Arterial tortuosity/ectasia' },
      { value: 'beighton-score', label: 'Beighton score', hasTextField: true },
      { value: 'bifid-uvula', label: 'Bifid uvula' },
      {
        value: 'chest-deformity',
        label: 'Chest deformity',
        subItems: [
          { value: 'pectus-carinatum', label: 'Pectus carinatum' },
          { value: 'pectus-excavatum', label: 'Pectus excavatum' },
        ],
        selectType: 'checkbox',
      },
      { value: 'congenital-hip-dislocation', label: 'Congenital hip dislocation' },
      { value: 'craniosynostosis', label: 'Craniosynostosis' },
      { value: 'dental-crowding', label: 'Dental crowding' },
      { value: 'dural-ectasia', label: 'Dural ectasia' },
      { value: 'high-palate', label: 'High palate' },
      {
        value: 'hollow-organ-rupture',
        label: 'Hollow organ rupture',
        subItems: [
          { value: 'intestinal-perforation', label: 'Unexplained intestinal perforation' },
          { value: 'uterine-rupture', label: 'Uterine rupture' },
          { value: 'hollow-organ-other', label: 'Other' },
        ],
        selectType: 'checkbox',
      },
      { value: 'hypertelorism', label: 'Hypertelorism' },
      { value: 'joint-contractures', label: 'Joint contractures' },
      { value: 'joint-dislocations', label: 'Joint dislocations' },
      {
        value: 'joint-hypermobility',
        label: 'Joint hypermobility',
        subItems: [
          { value: 'generalized', label: 'Generalized' },
          { value: 'severe', label: 'Severe' },
          { value: 'distal', label: 'Distal' },
        ],
        selectType: 'radio',
      },
      {
        value: 'mandibular-defect',
        label: 'Mandibular defect',
        subItems: [
          { value: 'micrognathia', label: 'Micrognathia' },
          { value: 'retrognathia', label: 'Retrognathia' },
        ],
        selectType: 'checkbox',
      },
      { value: 'meets-ghent-criteria', label: 'Meets Ghent criteria' },
      { value: 'midface-retrusion', label: 'Midface retrusion' },
      { value: 'mitral-valve-prolapse', label: 'Mitral valve prolapse' },
      { value: 'myopia', label: 'Myopia' },
      {
        value: 'ocular-findings',
        label: 'Ocular findings',
        subItems: [
          { value: 'blue-sclerae', label: 'Blue sclerae' },
          { value: 'brittle-cornea', label: 'Brittle cornea' },
          { value: 'ectopia-lentis', label: 'Ectopia lentis' },
          { value: 'retinal-detachment', label: 'Retinal detachment' },
          { value: 'thin-cornea', label: 'Thin cornea' },
        ],
        selectType: 'checkbox',
      },
      {
        value: 'orofacial-cleft',
        label: 'Orofacial cleft',
        subItems: [
          { value: 'cleft-palate', label: 'Cleft palate' },
          { value: 'cleft-lip', label: 'Cleft lip' },
        ],
        selectType: 'checkbox',
      },
      { value: 'osteoarthritis', label: 'Osteoarthritis' },
      { value: 'pes-planus', label: 'Pes planus' },
      {
        value: 'skin-findings',
        label: 'Skin findings',
        subItems: [
          { value: 'atrophic-scarring', label: 'Atrophic scarring' },
          { value: 'cutis-laxa', label: 'Cutis laxa' },
          { value: 'skin-fragility', label: 'Skin fragility' },
          { value: 'velvety-skin', label: 'Velvety skin' },
          { value: 'easy-bruising', label: 'Easy bruising' },
          { value: 'severe-bruising', label: 'Severe bruising' },
          { value: 'skin-hyperflexibility', label: 'Skin hyperflexibility' },
          { value: 'increased-palmar-wrinkling', label: 'Increased palmar wrinkling' },
        ],
        selectType: 'checkbox',
      },
      {
        value: 'spine-curvature',
        label: 'Spine curvature',
        subItems: [
          { value: 'scoliosis', label: 'Scoliosis' },
          { value: 'kyphoscoliosis', label: 'Kyphoscoliosis' },
          { value: 'kyphosis', label: 'Kyphosis' },
        ],
        selectType: 'radio',
      },
      { value: 'spontaneous-pneumothorax', label: 'Spontaneous pneumothorax' },
      { value: 'recurrent-fractures', label: 'Recurrent fractures' },
      { value: 'tall-stature', label: 'Tall stature' },
      { value: 'aortopathy-other', label: 'Other (Aortopathy)', hasTextField: true },
    ],
  },
  {
    category: 'Arrhythmia/Cardiomyopathy',
    hasYesNoUnknown: true,
    items: [
      { value: 'bradycardia', label: 'Bradycardia' },
      { value: 'conduction-system-disease', label: 'Conduction system disease' },
      { value: 'fatty-replacement', label: 'Fatty replacement of ventricular myocardial tissue' },
      { value: 'heart-transplant', label: 'Heart transplant' },
      { value: 'syncope', label: 'Syncope' },
      { value: 'torsades-de-pointes', label: 'Torsades de pointes' },
      { value: 'ventricular-tachycardia', label: 'Ventricular tachycardia' },
      { value: 'arrhythmia-other', label: 'Other (Arrhythmia)', hasTextField: true },
    ],
  },
  {
    category: 'Congenital Heart Defect',
    hasYesNoUnknown: true,
    items: [
      {
        value: 'aortic-valve-disease',
        label: 'Aortic valve disease',
        subItems: [
          { value: 'bicuspid', label: 'Bicuspid' },
          { value: 'coarctation', label: 'Coarctation' },
          { value: 'severe-disease', label: 'Severe disease' },
        ],
        selectType: 'checkbox',
      },
      { value: 'atrial-septal-defect', label: 'Atrial septal defect' },
      { value: 'heterotaxy', label: 'Heterotaxy' },
      { value: 'hypoplastic-left-heart', label: 'Hypoplastic left heart' },
      { value: 'patent-ductus-arteriosus', label: 'Patent ductus arteriosus' },
      { value: 'patent-foramen-ovale', label: 'Patent foramen ovale' },
      { value: 'tetralogy-of-fallot', label: 'Tetralogy of Fallot' },
      { value: 'ventricular-septal-defect', label: 'Ventricular septal defect' },
      { value: 'congenital-heart-other', label: 'Other (Congenital Heart)', hasTextField: true },
    ],
  },
  {
    category: 'Dyslipidemias',
    hasYesNoUnknown: true,
    items: [
      { value: 'atherosclerosis', label: 'Atherosclerosis' },
      { value: 'corneal-arcus', label: 'Corneal arcus' },
      { value: 'ldl-c-levels', label: 'LDL-C levels', hasTextField: true },
      { value: 'stroke', label: 'Stroke' },
      { value: 'triglycerides', label: 'Triglycerides', hasTextField: true },
      { value: 'xanthomatosis', label: 'Xanthomatosis' },
      { value: 'dyslipidemias-other', label: 'Other (Dyslipidemias)', hasTextField: true },
    ],
  },
  {
    category: 'ECG',
    hasYesNoUnknown: true,
    items: [
      { value: 'qtc-interval', label: 'QTc interval', hasTextField: true },
      { value: 'ecg-other', label: 'Other (ECG)', hasTextField: true },
    ],
  },
  {
    category: 'Echocardiogram or MRI',
    hasYesNoUnknown: true,
    items: [
      { value: 'aortic-diameter-z-score', label: 'Aortic diameter Z-score ≥2', hasTextField: true },
      { value: 'ef-percent', label: 'EF%', hasTextField: true },
      { value: 'lvedd', label: 'LVEDD', hasTextField: true },
      { value: 'max-lv-wall-thickness', label: 'Max LV wall thickness', hasTextField: true },
      { value: 'echo-other', label: 'Other (Echo/MRI)', hasTextField: true },
    ],
  },
  {
    category: 'HHT',
    hasYesNoUnknown: true,
    items: [
      { value: 'arteriovenous-malformation', label: 'Arteriovenous malformation' },
      { value: 'epistaxis', label: 'Epistaxis' },
      { value: 'telangiectasia', label: 'Telangiectasia' },
      { value: 'hht-other', label: 'Other (HHT)', hasTextField: true },
    ],
  },
  {
    category: 'Other',
    hasYesNoUnknown: true,
    items: [
      { value: 'angiokeratomas', label: 'Angiokeratomas' },
      { value: 'anhydrosis', label: 'Anhydrosis' },
      { value: 'cafe-au-lait-macules', label: 'Café-au-lait macules' },
      { value: 'cystic-hygroma', label: 'Cystic hygroma' },
      { value: 'downslanted-palpebral-fissures', label: 'Downslanted palpebral fissures' },
      { value: 'elevated-cpk', label: 'Elevated creatine phosphokinase (CPK/CK)' },
      {
        value: 'hearing-impairment',
        label: 'Hearing impairment',
        subItems: [
          { value: 'sensorineural', label: 'Sensorineural' },
          { value: 'conductive', label: 'Conductive' },
        ],
        selectType: 'checkbox',
      },
      { value: 'hypotonia', label: 'Hypotonia' },
      { value: 'increased-nuchal-translucency', label: 'Increased nuchal translucency' },
      { value: 'intellectual-disability', label: 'Intellectual disability' },
      { value: 'keratoconus', label: 'Keratoconus' },
      { value: 'muscle-weakness', label: 'Muscle weakness' },
      { value: 'myopathy', label: 'Myopathy' },
      { value: 'renal-insufficiency', label: 'Renal insufficiency' },
      { value: 'short-neck', label: 'Short neck' },
      { value: 'thromboembolism', label: 'Thromboembolism' },
    ],
  },
]

// Cardiology Test Catalog — from pages 3-5 of TRF904-20
// Clinical Area: Cardiology and Neurology
export const CARDIOLOGY_TEST_PANELS = [
  {
    category: 'Hereditary Hemorrhagic Telangiectasia and Vascular Malformations',
    tests: [
      {
        code: '02352',
        name: 'Invitae Hereditary Hemorrhagic Telangiectasia and Vascular Malformations Panel',
        genes: 6,
        geneList: 'ACVRL1, ENG, EPHB4, GDF2, RASA1, SMAD4',
      },
    ],
  },
  {
    category: 'Arrhythmia and Cardiomyopathy',
    tests: [
      {
        code: '02101',
        name: 'Invitae Arrhythmia and Cardiomyopathy Comprehensive Panel',
        genes: 100,
        geneList:
          'ABCC9, ACADVL, ACTC1, ACTN2, AGL, ALMS1, ALPK3, BAG3, BRAF, CACNA1C, CACNA1D, CALM1, CALM2, CALM3, CASQ2, CBL, CDH2, CPT2, CRYAB, CSRP3, DES, DMD, DNAJC19, DOLK, DSC2, DSG2, DSP, ELAC2, EMD, EYA4, FHL1, FKRP, FKTN, FLNC, GAA, GATA4, GATA5, GJA5, GLA, HCN4, HRAS, JUP, KCNE1, KCNH2, KCNJ2, KCNQ1, KRAS, LAMP2, LMNA, LZTR1, MAP2K1, MAP2K2, MRAS, MTO1, MYBPC3, MYH7, MYL2, MYL3, MYL4, MYLK3, NF1, NKX2-5, NRAS, PCCA, PCCB, PKP2, PLN, PPA2, PPCS, PPP1CB, PRKAG2, PTPN11, RAF1, RASA1, RBM20, RIT1, RYR2, SCN5A, SDHA, SGCD, SHOC2, SLC22A5, SOS1, SOS2, SPRED1, TAZ, TBX20, TCAP, TMEM43, TMEM70, TNNC1, TNNI3, TNNI3K, TNNT2, TPM1, TRDN, TRPM4, TTN, TTR, VCL',
      },
      {
        code: '02101.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 57,
        geneList:
          'A2ML1, AKAP9, ANK2, ANKRD1, CACNA2D1, CACNB2, CALR3, CAV3, CHRM2, CTF1, CTNNA3, DTNA, FHL2, GATA6, GATAD1, GPD1L, HAND1, ILK, JPH2, KCNA5, KCND3, KCNE2, KCNE3, KCNE5, KCNJ5, KCNJ8, KCNK3, KIF20A, KLF10, LAMA4, LDB3, LRRC10, MAP3K8, MED12, MYH6, MYLK2, MYOM1, MYOZ2, MYPN, NEBL, NEXN, NPPA, PDLIM3, PLEKHM2, PRDM16, RANGRF, RASA2, RRAS, SCN10A, SCN1B, SCN2B, SCN3B, SCN4B, SLMAP, SNTA1, TMPO, TXNRD2',
        indent: true,
      },
      {
        code: '02101.4',
        name: 'Add-on sudden unexpected death in epilepsy (SUDEP) genes for arrhythmia and cardiomyopathy',
        genes: 11,
        geneList: 'DEPDC5, KCNA1, KCNQ2, KCNQ3, KCNT1, PCDH19, PRRT2, SCN1A, SCN8A, SCN9A, SLC2A1',
        indent: true,
      },
    ],
  },
  {
    category: 'Arrhythmia',
    tests: [
      {
        code: '02211',
        name: 'Invitae Long QT Syndrome Panel',
        genes: 10,
        geneList: 'CACNA1C, CALM1, CALM2, CALM3, KCNE1, KCNH2, KCNJ2, KCNQ1, SCN5A, TRDN',
      },
      {
        code: '02211.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 7,
        geneList: 'AKAP9, ANK2, CAV3, KCNE2, KCNJ5, SCN4B, SNTA1',
        indent: true,
      },
      {
        code: '02213',
        name: 'Invitae Catecholaminergic Polymorphic Ventricular Tachycardia Panel',
        genes: 7,
        geneList: 'CALM1, CALM2, CALM3, CASQ2, KCNJ2, RYR2, TRDN',
      },
      {
        code: '02214',
        name: 'Invitae Short QT Syndrome Panel',
        genes: 3,
        geneList: 'KCNH2, KCNJ2, KCNQ1',
      },
      {
        code: '02214.1',
        name: 'Add-on preliminary-evidence gene',
        genes: 3,
        geneList: 'CACNA1C, CACNA2D1, CACNB2',
        indent: true,
      },
      {
        code: '02201',
        name: 'Invitae Arrhythmia Comprehensive Panel',
        genes: 41,
        geneList:
          'ABCC9, ACTN2, BAG3, CACNA1C, CACNA1D, CALM1, CALM2, CALM3, CASQ2, CDH2, DES, DSC2, DSG2, DSP, EMD, FLNC, GATA4, GATA5, GJA5, HCN4, JUP, KCNE1, KCNH2, KCNJ2, KCNQ1, LMNA, MYL4, NKX2-5, PKP2, PLN, PPA2, PRKAG2, RBM20, RYR2, SCN5A, TMEM43, TNNI3, TNNT2, TRDN, TRPM4, TTN',
      },
      {
        code: '02201.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 28,
        geneList:
          'AKAP9, ANK2, ANKRD1, CACNA2D1, CACNB2, CAV3, CTNNA3, GATA6, GPD1L, KCNA5, KCND3, KCNE2, KCNE3, KCNE5, KCNJ5, KCNJ8, KCNK3, LDB3, NPPA, PDLIM3, RANGRF, SCN10A, SCN1B, SCN2B, SCN3B, SCN4B, SLMAP, SNTA1',
        indent: true,
      },
      {
        code: '02201.2',
        name: 'Add-on sudden unexpected death in epilepsy (SUDEP) genes for arrhythmia',
        genes: 11,
        geneList: 'DEPDC5, KCNA1, KCNQ2, KCNQ3, KCNT1, PCDH19, PRRT2, SCN1A, SCN8A, SCN9A, SLC2A1',
        indent: true,
      },
      {
        code: '02212',
        name: 'Invitae Brugada Syndrome Test',
        genes: 1,
        geneList: 'SCN5A',
      },
      {
        code: '02212.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 19,
        geneList:
          'ABCC9, CACNA1C, CACNA2D1, CACNB2, GPD1L, HCN4, KCND3, KCNH2, KCNE3, KCNE5, KCNJ8, PKP2, RANGRF, SCN10A, SCN1B, SCN2B, SCN3B, SLMAP, TRPM4',
        indent: true,
      },
    ],
  },
  {
    category: 'Cardiomyopathy',
    tests: [
      {
        code: '02261',
        name: 'Invitae Hypertrophic Cardiomyopathy Panel',
        genes: 30,
        geneList:
          'ACADVL, ACTC1, ACTN2, AGL, ALPK3, BAG3, CACNA1C, CPT2, CSRP3, DES, ELAC2, FHL1, FLNC, GAA, GLA, LAMP2, MTO1, MYBPC3, MYH7, MYL2, MYL3, PLN, PRKAG2, TCAP, TNNC1, TNNI3, TNNT2, TPM1, TTR, VCL',
      },
      {
        code: '02261.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 14,
        geneList:
          'ANKRD1, CALR3, CAV3, GATA4, JPH2, KLF10, LDB3, MYH6, MYLK2, MYOM1, MYOZ2, MYPN, NEXN, PDLIM3',
        indent: true,
      },
      {
        code: '02265',
        name: 'Invitae Hereditary Transthyretin-mediated amyloidosis (hATTR amyloidosis) Test',
        genes: 1,
        geneList: 'TTR',
      },
      {
        code: '04151',
        name: 'Invitae RASopathies and Noonan Spectrum Disorders Panel',
        genes: 28,
        geneList:
          'A2ML1, ACTB, ACTG1, BRAF, CBL, HRAS, KAT6B, KRAS, LZTR1, MAP2K1, MAP2K2, MRAS, NF1, NRAS, NSUN2, PPP1CB, PTPN11, RAF1, RASA1, RASA2, RIT1, RRAS, RRAS2, SHOC2, SOS1, SOS2, SPRED1, YWHAZ',
      },
      {
        code: '02262',
        name: 'Invitae Dilated Cardiomyopathy and Left Ventricular Noncompaction Panel',
        genes: 54,
        geneList:
          'ABCC9, ACADVL, ACTC1, ACTN2, ALMS1, ALPK3, BAG3, CPT2, CRYAB, CSRP3, DES, DMD, DNAJC19, DOLK, DSC2, DSG2, DSP, EMD, EYA4, FKRP, FKTN, FLNC, HCN4, JUP, LAMP2, LMNA, MYBPC3, MYH7, MYLK3, PCCA, PCCB, PKP2, PLN, PPCS, RAF1, RBM20, RYR2, SCN5A, SDHA, SGCD, SLC22A5, TAZ, TBX20, TCAP, TMEM43, TMEM70, TNNC1, TNNI3, TNNI3K, TNNT2, TPM1, TTN, TTR, VCL',
      },
      {
        code: '02262.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 26,
        geneList:
          'ANKRD1, CAV3, CHRM2, CTF1, DTNA, FHL2, GATA4, GATA6, GATAD1, HAND1, ILK, LAMA4, LDB3, LRRC10, MED12, MYH6, MYPN, NEBL, NEXN, NKX2-5, NPPA, PDLIM3, PLEKHM2, PRDM16, TMPO, TXNRD2',
        indent: true,
      },
      {
        code: '02251',
        name: 'Invitae Cardiomyopathy Comprehensive Panel',
        genes: 82,
        geneList:
          'ABCC9, ACADVL, ACTC1, ACTN2, AGL, ALMS1, ALPK3, BAG3, BRAF, CACNA1C, CBL, CPT2, CRYAB, CSRP3, DES, DMD, DNAJC19, DOLK, DSC2, DSG2, DSP, ELAC2, EMD, EYA4, FHL1, FKRP, FKTN, FLNC, GAA, GLA, HCN4, HRAS, JUP, KRAS, LAMP2, LMNA, LZTR1, MAP2K1, MAP2K2, MRAS, MTO1, MYBPC3, MYH7, MYL2, MYL3, MYLK3, NF1, NRAS, PCCA, PCCB, PKP2, PLN, PPCS, PPP1CB, PRKAG2, PTPN11, RAF1, RASA1, RBM20, RIT1, RYR2, SCN5A, SDHA, SGCD, SHOC2, SLC22A5, SOS1, SOS2, SPRED1, TAZ, TBX20, TCAP, TMEM43, TMEM70, TNNC1, TNNI3, TNNI3K, TNNT2, TPM1, TTN, TTR, VCL',
      },
      {
        code: '02251.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 39,
        geneList:
          'A2ML1, ANKRD1, CALR3, CAV3, CDH2, CHRM2, CTF1, CTNNA3, DTNA, FHL2, GATA4, GATA6, GATAD1, HAND1, ILK, JPH2, KIF20A, KLF10, LAMA4, LDB3, LRRC10, MAP3K8, MED12, MYH6, MYLK2, MYOM1, MYOZ2, MYPN, NEBL, NEXN, NKX2-5, NPPA, PDLIM3, PLEKHM2, PRDM16, RASA2, RRAS, TMPO, TXNRD2',
        indent: true,
      },
      {
        code: '02263',
        name: 'Invitae Arrhythmogenic Cardiomyopathy Panel',
        genes: 22,
        geneList:
          'ACTN2, BAG3, CDH2, DES, DSC2, DSG2, DSP, EMD, FLNC, JUP, LMNA, PKP2, PLN, PPA2, PRKAG2, RBM20, RYR2, SCN5A, TMEM43, TNNI3, TNNT2, TTN',
      },
      {
        code: '02263.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 5,
        geneList: 'ANKRD1, CTNNA3, LDB3, NKX2-5, PDLIM3',
        indent: true,
      },
      {
        code: '05201',
        name: 'Invitae Hereditary Hemochromatosis Panel',
        genes: 6,
        geneList: 'FTH1, HAMP, HFE, HJV, SLC40A1, TFR2',
      },
    ],
  },
  {
    category: 'Aortopathy',
    tests: [
      {
        code: '02301',
        name: 'Invitae Aortopathy Comprehensive Panel',
        genes: 29,
        geneList:
          'ACTA2, ADAMTS10, BGN, CBS, COL3A1, COL5A1, COL5A2, EFEMP2, FBN1, FBN2, FLNA, FOXE3, LOX, MED12, MFAP5, MYH11, MYLK, NOTCH1, PLOD1, PRKG1, SKI, SLC2A10, SMAD2, SMAD3, SMAD4, TGFB2, TGFB3, TGFBR1, TGFBR2',
      },
      {
        code: '02301.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 6,
        geneList: 'ARIH1, HCN4, LTBP3, MAT2A, PLOD3, SMAD6',
        indent: true,
      },
      {
        code: '02311',
        name: 'Invitae Loeys-Dietz Syndrome Panel',
        genes: 7,
        geneList: 'FBN1, SMAD2, SMAD3, TGFB2, TGFB3, TGFBR1, TGFBR2',
      },
      {
        code: '02313',
        name: 'Invitae Ehlers-Danlos Syndrome Panel',
        genes: 17,
        geneList:
          'ADAMTS2, ATP7A, B3GALT6, B4GALT7, CHST14, COL12A1, COL1A1, COL1A2, COL3A1, COL5A1, COL5A2, CRTAP, FKBP14, FLNA, P3H1, PLOD1, SLC39A13',
      },
      {
        code: '02312',
        name: 'Invitae Marfan Syndrome Test',
        genes: 1,
        geneList: 'FBN1',
      },
    ],
  },
  {
    category: 'Lipid Disorders',
    tests: [
      {
        code: '02401',
        name: 'Invitae Familial Hypercholesterolemia Panel',
        genes: 4,
        geneList: 'APOB, LDLR, LDLRAP1, PCSK9',
      },
      {
        code: '53698',
        name: 'Invitae Comprehensive Lipidemia Panel',
        genes: 25,
        geneList:
          'ABCA1, ABCG5, ABCG8, ANGPTL3, APOA1, APOA5, APOB, APOC2, CETP, CREB3L3, CYP27A1, GPD1, GPIHBP1, LCAT, LDLR, LDLRAP1, LIPA, LIPG, LMF1, LPL, LRP6, MTTP, PCSK9, PNPLA2, SAR1B',
      },
      {
        code: '53698.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 11,
        geneList: 'APOA4, APOC3, CYP7A1, GALNT2, GCKR, LIPC, LIPI, MYLIP, PLTP, SCARB1, ZHX3',
        indent: true,
      },
    ],
  },
  {
    category: 'Pulmonary Hypertension',
    tests: [
      {
        code: '02351',
        name: 'Invitae Pulmonary Arterial Hypertension Panel',
        genes: 12,
        geneList:
          'ACVRL1, AQP1, ATP13A3, BMPR2, CAV1, EIF2AK4, ENG, GDF2, KCNK3, SMAD9, SOX17, TBX4',
      },
      {
        code: '02351.1',
        name: 'Add-on preliminary-evidence genes',
        genes: 2,
        geneList: 'BMPR1B, KCNA5',
        indent: true,
      },
    ],
  },
  {
    category: 'Congenital Heart Disease',
    tests: [
      {
        code: '04151-chd',
        name: 'Invitae RASopathies and Noonan Spectrum Disorders Panel',
        genes: 28,
        geneList:
          'A2ML1, ACTB, ACTG1, BRAF, CBL, HRAS, KAT6B, KRAS, LZTR1, MAP2K1, MAP2K2, MRAS, NF1, NRAS, NSUN2, PPP1CB, PTPN11, RAF1, RASA1, RASA2, RIT1, RRAS, RRAS2, SHOC2, SOS1, SOS2, SPRED1, YWHAZ',
      },
      {
        code: '04204',
        name: 'Invitae Congenital Heart Disease Panel',
        genes: 55,
        geneList:
          'ACTC1, ACVR2B, ALMS1, BCOR, BRAF, CASZ1, CBL, CHD7, CRELD1, ELN, FOXH1, GATA4, GATA5, GATA6, GDF1, GJA1, GPC3, HAND1, HAND2, HRAS, JAG1, KDM6A, KMT2D, KRAS, LEFTY2, MAP2K1, MAP2K2, MED13L, MEIS2, MESP1, MYH6, NFATC1, NKX2-5, NKX2-6, NODAL, NOTCH1, NR2F2, NRAS, NSD1, PLD1, PTPN11, RAF1, RBFOX2, RIT1, ROBO1, SHOC2, SMAD6, SOS1, TAB2, TBX1, TBX20, TBX5, TFAP2B, ZFPM2, ZIC3',
      },
    ],
  },
] as const

// ---------------------------------------------------------------------------
// Cardiology-specific state types
// ---------------------------------------------------------------------------

export interface CardiacClinicalHistory {
  isAffected: 'yes' | 'no' | ''
  ageAtDiagnosis: string
  briefDescription: string
  hasHematologicalMalignancy: 'yes' | 'no' | ''
  hasPriorGeneticTesting: 'yes' | 'no' | ''
  priorGeneticTestResults: string
  // Clinical diagnosis checkboxes
  clinicalDiagnoses: string[]
  clinicalDiagnosisOther: string
  taadDetails: string // TAAD sub-field
  // Clinical information checklist — category status + selected items + text fields
  categoryStatus: Record<string, 'yes' | 'no' | 'unknown' | ''>
  selectedItems: Record<string, string[]> // category -> selected item values
  subSelections: Record<string, string[]> // item value -> selected sub-item values
  textFields: Record<string, string> // item value -> text field value
}

export function createEmptyCardiacHistory(): CardiacClinicalHistory {
  return {
    isAffected: '',
    ageAtDiagnosis: '',
    briefDescription: '',
    hasHematologicalMalignancy: '',
    hasPriorGeneticTesting: '',
    priorGeneticTestResults: '',
    clinicalDiagnoses: [],
    clinicalDiagnosisOther: '',
    taadDetails: '',
    categoryStatus: {},
    selectedItems: {},
    subSelections: {},
    textFields: {},
  }
}
